import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { RoleGuideBanner } from '../components/RoleGuideBanner';
import ProfessionalAdvisor from '../components/ProfessionalAdvisor';
import DealTracker from '../components/DealTracker';
import FindProfessionals from '../components/FindProfessionals';
import PropertyCard from '../components/PropertyCard';
import DashboardTabs from '../components/DashboardTabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  UserCheck, Briefcase, Home, FileText, Plus, Eye, Users, Code, ExternalLink, CheckCircle,
  AlertCircle, Clock, Shield, ShieldCheck, Upload, Loader2, XCircle, Building2, Calendar, HelpCircle,
  MessageCircle, ArrowRight, Crown, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import SocialShareButtons from '../components/SocialShareButtons';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import GettingStartedGuide from '../components/GettingStartedGuide';
import RecoveryDiscountBanner from '../components/RecoveryDiscountBanner';
import AffiliateWidget from '../components/AffiliateWidget';
import VerifiedPartnerCTA from '../components/VerifiedPartnerCTA';
import SubscriptionModal from '../components/SubscriptionModal';
import SubscriptionPlans from '../components/SubscriptionPlans';
import PowerPartnerUpgrade from '../components/PowerPartnerUpgrade';
import InviteTeaserCard from '../components/InviteTeaserCard';
import { useQueryTab } from '../hooks/useQueryTab';
import { BulkListingUploader } from '../components/agent/BulkListingUploader';
import LocalProsPanel from '../components/LocalProsPanel';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============ AGENT VERIFICATION SECTION ============
const AgentVerificationBanner = ({ status, onSubmit }) => {
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '', license_number: '', professional_body: '',
    years_experience: '', specializations: [], areas_covered: []
  });
  const [newSpec, setNewSpec] = useState('');
  const [newArea, setNewArea] = useState('');

  const handleSubmit = async () => {
    if (!formData.company_name || !formData.license_number || !formData.professional_body) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null
      };
      const res = await axios.post(`${API_URL}/api/agent/verify`, payload);
      toast.success(res.data.message);
      onSubmit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    }
    setSubmitting(false);
  };

  if (status === 'approved') {
    return (
      <div className="luxury-card p-5 bg-emerald-50 border-emerald-200 flex items-center gap-3 mb-6" data-testid="agent-verified-banner">
        <ShieldCheck className="h-6 w-6 text-emerald-600 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-emerald-700">Verified Agent</p>
          <p className="text-xs text-emerald-600">Your credentials are verified. You can list and manage properties.</p>
        </div>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="luxury-card p-5 bg-amber-50 border-amber-200 flex items-center gap-3 mb-6" data-testid="agent-pending-banner">
        <Clock className="h-6 w-6 text-amber-600 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-amber-700">Verification Pending</p>
          <p className="text-xs text-amber-600">Your credentials are being reviewed by our admin team. You'll be notified once approved.</p>
        </div>
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className="space-y-4 mb-6">
        <div className="luxury-card p-5 bg-red-50 border-red-200 flex items-center gap-3" data-testid="agent-rejected-banner">
          <XCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-700">Verification Not Approved</p>
            <p className="text-xs text-red-600">Please resubmit with correct credentials.</p>
          </div>
          <Button onClick={() => setShowForm(true)} className="bg-red-600 text-white hover:bg-red-700 text-xs" data-testid="resubmit-verification-btn">
            Resubmit
          </Button>
        </div>
        {showForm && renderForm()}
      </div>
    );
  }

  // Not submitted
  const renderForm = () => (
    <div className="luxury-card p-6 space-y-4 mb-6" data-testid="agent-verification-form">
      <h3 className="text-lg font-semibold text-[#0F172A] flex items-center gap-2">
        <Shield className="h-5 w-5 text-[#E07A3A]" />
        Submit Your Credentials
      </h3>
      <p className="text-sm text-slate-500">Provide your business details for admin verification. AI will analyze your credentials for faster approval.</p>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-slate-500 tracking-wider uppercase">Company / Agency Name *</Label>
          <Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})}
            placeholder="Smith & Partners" className="h-11 mt-1 bg-[#F8FAFC]" data-testid="verify-company-name" />
        </div>
        <div>
          <Label className="text-xs text-slate-500 tracking-wider uppercase">License Number *</Label>
          <Input value={formData.license_number} onChange={(e) => setFormData({...formData, license_number: e.target.value})}
            placeholder="NAEA-12345" className="h-11 mt-1 bg-[#F8FAFC]" data-testid="verify-license-number" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-slate-500 tracking-wider uppercase">Professional Body *</Label>
          <Select value={formData.professional_body} onValueChange={(v) => setFormData({...formData, professional_body: v})}>
            <SelectTrigger className="h-11 mt-1 bg-[#F8FAFC]" data-testid="verify-professional-body"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="NAEA">NAEA Propertymark</SelectItem>
              <SelectItem value="ARLA">ARLA Propertymark</SelectItem>
              <SelectItem value="RICS">RICS</SelectItem>
              <SelectItem value="RERA">RERA</SelectItem>
              <SelectItem value="RECO">RECO</SelectItem>
              <SelectItem value="CREA">CREA</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500 tracking-wider uppercase">Years of Experience</Label>
          <Input type="number" value={formData.years_experience} onChange={(e) => setFormData({...formData, years_experience: e.target.value})}
            placeholder="e.g. 5" className="h-11 mt-1 bg-[#F8FAFC]" data-testid="verify-experience" />
        </div>
      </div>

      <div>
        <Label className="text-xs text-slate-500 tracking-wider uppercase">Specializations</Label>
        <div className="flex gap-2 mt-1">
          <Input value={newSpec} onChange={(e) => setNewSpec(e.target.value)} placeholder="e.g. Residential, Luxury, Commercial"
            className="h-11 bg-[#F8FAFC]" onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); if (newSpec.trim()) { setFormData({...formData, specializations: [...formData.specializations, newSpec.trim()]}); setNewSpec(''); } }
            }} />
          <Button type="button" onClick={() => { if (newSpec.trim()) { setFormData({...formData, specializations: [...formData.specializations, newSpec.trim()]}); setNewSpec(''); } }}
            className="bg-[#E07A3A] text-white h-11"><Plus className="h-4 w-4" /></Button>
        </div>
        {formData.specializations.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {formData.specializations.map((s, i) => (
              <span key={`spec-${s}-${i}`} className="px-2 py-1 bg-[#FEF3EC] text-[#E07A3A] text-xs font-medium flex items-center gap-1">
                {s}<button type="button" onClick={() => setFormData({...formData, specializations: formData.specializations.filter((_, idx) => idx !== i)})} className="hover:text-red-500">&times;</button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <Label className="text-xs text-slate-500 tracking-wider uppercase">Areas Covered</Label>
        <div className="flex gap-2 mt-1">
          <Input value={newArea} onChange={(e) => setNewArea(e.target.value)} placeholder="e.g. London, Manchester, Birmingham"
            className="h-11 bg-[#F8FAFC]" onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); if (newArea.trim()) { setFormData({...formData, areas_covered: [...formData.areas_covered, newArea.trim()]}); setNewArea(''); } }
            }} />
          <Button type="button" onClick={() => { if (newArea.trim()) { setFormData({...formData, areas_covered: [...formData.areas_covered, newArea.trim()]}); setNewArea(''); } }}
            className="bg-[#E07A3A] text-white h-11"><Plus className="h-4 w-4" /></Button>
        </div>
        {formData.areas_covered.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {formData.areas_covered.map((a, i) => (
              <span key={`area-${a}-${i}`} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium flex items-center gap-1">
                {a}<button type="button" onClick={() => setFormData({...formData, areas_covered: formData.areas_covered.filter((_, idx) => idx !== i)})} className="hover:text-red-500">&times;</button>
              </span>
            ))}
          </div>
        )}
      </div>

      <Button onClick={handleSubmit} disabled={submitting} className="bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12 w-full tracking-wider text-sm font-semibold" data-testid="submit-verification-btn">
        {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</> : 'Submit for Verification'}
      </Button>
    </div>
  );

  return (
    <div className="mb-6">
      <div className="luxury-card p-5 bg-blue-50 border-blue-200 flex items-start gap-3" data-testid="agent-unverified-banner">
        <AlertCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-blue-700">Verification Required</p>
          <p className="text-xs text-blue-600 mt-1">Submit your business credentials to start listing properties. Our AI will analyze your documents for faster approval.</p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="bg-blue-600 text-white hover:bg-blue-700 text-xs" data-testid="start-verification-btn">
            Get Verified
          </Button>
        )}
      </div>
      {showForm && renderForm()}
    </div>
  );
};

// ============ TRIAL TRACKER ============
const TrialTracker = ({ trialData, onRelease, onSubscribe }) => {
  if (!trialData) return null;

  const { trial_status, days_active, free_deals_target, free_deals_completed, active_deals, can_list } = trialData;

  if (trial_status === 'subscribed') {
    return (
      <div className="luxury-card p-5 bg-emerald-50 border-emerald-200 mb-6 flex items-center gap-3" data-testid="trial-subscribed">
        <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-emerald-700">Subscribed</p>
          <p className="text-xs text-emerald-600">Full access. List unlimited properties.</p>
        </div>
      </div>
    );
  }

  if (trial_status === 'deal_completed' || trial_status === 'expired') {
    return (
      <div className="luxury-card p-5 bg-amber-50 border-amber-300 mb-6" data-testid="trial-expired">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-700">Free Trial Complete</p>
            <p className="text-xs text-amber-600 mt-1">
              You closed your free deal in <strong>{days_active} days</strong>. Subscribe to keep your listings exclusive.
            </p>
            <p className="text-xs text-red-600 mt-2 font-semibold">
              Without a subscription, your listings will become open for other agents to claim or buyers to go direct.
            </p>
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <Button onClick={onSubscribe} className="bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs tracking-wider px-6" data-testid="subscribe-btn">
            Subscribe Now
          </Button>
          {trial_status === 'deal_completed' && (
            <Button variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 text-xs" onClick={onRelease} data-testid="release-listings-btn">
              Release My Listings
            </Button>
          )}
        </div>
      </div>
    );
  }

  // free_trial status
  const progress = free_deals_target > 0 ? Math.min(100, (free_deals_completed / free_deals_target) * 100) : 0;

  return (
    <div className="luxury-card p-5 mb-6" data-testid="trial-tracker">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-[#E07A3A]" />
          <p className="text-sm font-semibold text-[#0F172A]">Free Trial</p>
        </div>
        <span className="text-xs bg-[#FEF3EC] text-[#E07A3A] px-3 py-1 font-bold tracking-wider">DAY {days_active || 1}</span>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="text-center p-3 bg-slate-50">
          <p className="text-2xl font-bold text-[#0F172A]">{free_deals_completed}/{free_deals_target}</p>
          <p className="text-[10px] text-slate-500 tracking-wider uppercase mt-1">Free Deals Used</p>
          <div className="w-full bg-slate-200 h-1.5 mt-2">
            <div className="bg-[#E07A3A] h-1.5 transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <div className="text-center p-3 bg-slate-50">
          <p className="text-2xl font-bold text-[#E07A3A]">{active_deals}</p>
          <p className="text-[10px] text-slate-500 tracking-wider uppercase mt-1">Active Deals</p>
        </div>
        <div className="text-center p-3 bg-slate-50">
          <p className="text-2xl font-bold text-[#0F172A]">{days_active || 1}</p>
          <p className="text-[10px] text-slate-500 tracking-wider uppercase mt-1">Days Active</p>
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-3">Close your first deal free. After that, subscribe to keep your listings exclusive.</p>
    </div>
  );
};


// ============ OPEN LISTINGS TAB ============
const OpenListingsSection = ({ onClaim }) => {
  const [openListings, setOpenListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOpen = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/properties/open-listings`);
        setOpenListings(res.data || []);
      } catch (err) {
        logger.error('Failed to fetch open listings:', err);
      }
      setLoading(false);
    };
    fetchOpen();
  }, []);

  if (loading) return <div className="flex justify-center py-8"><div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" /></div>;

  if (openListings.length === 0) {
    return (
      <div className="luxury-card p-12 text-center">
        <Home className="h-12 w-12 text-slate-200 mx-auto mb-3" />
        <p className="text-slate-500 font-medium">No open listings available</p>
        <p className="text-xs text-slate-400 mt-2">Open listings appear when agents release their properties after their free trial ends.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="open-listings-section">
      <p className="text-sm text-slate-500">These properties are available for you to claim and represent. The original agent's trial expired.</p>
      {openListings.map(p => (
        <div key={p.id} className="luxury-card p-4 flex flex-col sm:flex-row gap-4 items-start border-l-4 border-l-amber-400" data-testid={`open-listing-${p.id}`}>
          <div className="w-full sm:w-32 h-24 bg-slate-100 flex-shrink-0 overflow-hidden">
            {p.images?.[0] ? (
              <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-300"><Home className="h-8 w-8" /></div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-[#1A1A1A]">{p.title}</h3>
            <p className="text-xs text-slate-400 mt-1">{p.city} | {p.bedrooms} bed | Previously: {p.original_agent_name}</p>
            <span className={`text-[10px] px-2 py-0.5 font-semibold mt-1 inline-block ${p.listing_type === 'rent' ? 'bg-blue-50 text-blue-600' : 'bg-[#FEF3EC] text-[#E07A3A]'}`}>
              {p.listing_type === 'rent' ? 'RENTAL' : 'SALE'}
            </span>
          </div>
          <Button onClick={() => onClaim(p.id)} className="bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs" data-testid={`claim-listing-${p.id}`}>
            Verify & Join Network
          </Button>
        </div>
      ))}
    </div>
  );
};


// ============ EMBED CODE SECTION (Preserved) ============
const EmbedCodeSection = () => {
  const [embedData, setEmbedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestSent, setRequestSent] = useState(false);
  const [requestStatus, setRequestStatus] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchEmbed = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/agent/embed-code`);
        setEmbedData(res.data);
      } catch (err) { logger.error('Failed to load embed data:', err); }
      setLoading(false);
    };
    fetchEmbed();
  }, []);

  const requestEmbed = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/agent/request-embed`, { website_url: websiteUrl });
      setRequestSent(true);
      setRequestStatus(res.data.status);
      toast.success(res.data.message);
    } catch (err) {
      toast.error('Failed to send request. Please try again.');
    }
    setSending(false);
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  const slug = embedData?.profile_url?.split('/agent/')[1] || '';
  const agentName = embedData?.agent_name || 'Agent';

  return (
    <div className="space-y-6" data-testid="embed-section">
      <div className="luxury-card p-6">
        <SocialShareButtons profileUrl={embedData?.profile_url || ''} agentName={agentName} slug={slug} />
      </div>
      <div className="luxury-card p-6">
        <h3 className="text-lg font-semibold text-[#0F172A] mb-2 flex items-center gap-2"><ExternalLink className="h-5 w-5 text-[#E07A3A]" />Your Public Profile</h3>
        <p className="text-sm text-slate-500 mb-4">This is your profile page on Shopperden. Preview it below.</p>
        <div className="flex gap-2">
          <input readOnly value={embedData?.profile_url || ''} className="flex-1 bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-600 font-mono" data-testid="profile-url" />
          <a href={embedData?.profile_url} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#0F172A] text-white hover:bg-[#1e293b] text-xs tracking-wider" data-testid="preview-profile-btn">
              <Eye className="h-3.5 w-3.5 mr-1.5" />Preview
            </Button>
          </a>
        </div>
      </div>
      <div className="luxury-card p-6" data-testid="request-embed-section">
        <h3 className="text-lg font-semibold text-[#0F172A] mb-2 flex items-center gap-2">
          <Code className="h-5 w-5 text-[#E07A3A]" />Website Widget
        </h3>
        <p className="text-sm text-slate-500 mb-4">Have your own website? We can add a Shopperden widget that shows your property listings directly on your site.</p>
        {(() => {
          if (requestSent || requestStatus === 'pending') return (
            <div className="bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3" data-testid="request-sent-msg">
              <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-emerald-700">Request Received</p>
                <p className="text-xs text-emerald-600 mt-1">Our team will set up your website widget and contact you within 24 hours.</p>
              </div>
            </div>
          );
          if (requestStatus === 'completed') return (
            <div className="bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-700">Widget Already Set Up</p>
                <p className="text-xs text-blue-600 mt-1">Your website widget has been configured.</p>
              </div>
            </div>
          );
          return (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Your website address (optional)</label>
                <input type="url" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="e.g. www.myagency.com"
                  className="w-full bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm text-slate-600 focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
                  data-testid="website-url-input" />
              </div>
              <Button onClick={requestEmbed} disabled={sending}
                className="bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs tracking-wider px-6 py-5" data-testid="request-embed-btn">
                {sending ? 'Sending...' : 'Request Website Widget Setup'}
              </Button>
            </div>
          );
        })()}
      </div>
      {/* No guide here - moved to main dashboard */}
    </div>
  );
};

// ============ MAIN AGENT DASHBOARD ============
const AgentDashboard = () => {
  const navigate = useNavigate();
  const { token: authToken, user } = useAuth();
  const [activeTab, setActiveTab] = useQueryTab('my-listings', ['my-listings','deals','leads','availability','consultations','verification','referrals','subscription','profile','share','open-listings','find-pros','embed','professional','marketplace','bulk-upload','upgrade']);

  const agentTabs = useMemo(() => [
    { id: 'my-listings', label: 'My Listings', shortLabel: 'Listings', icon: <Home className="h-4 w-4" />, testId: 'tab-agent-my-listings' },
    { id: 'bulk-upload', label: 'Bulk Upload', shortLabel: 'Bulk', icon: <Upload className="h-4 w-4" />, testId: 'tab-agent-bulk-upload' },
    { id: 'open-listings', label: 'Available Properties', shortLabel: 'Available', icon: <Eye className="h-4 w-4" />, testId: 'tab-agent-open-listings' },
    { id: 'deals', label: 'My Deals', shortLabel: 'Deals', icon: <FileText className="h-4 w-4" />, testId: 'tab-agent-deals' },
    { id: 'availability', label: 'My Diary', shortLabel: 'Diary', icon: <Calendar className="h-4 w-4" />, testId: 'tab-agent-availability' },
    { id: 'subscription', label: 'Subscription', shortLabel: 'Plan', icon: <ShieldCheck className="h-4 w-4" />, testId: 'tab-agent-subscription' },
    { id: 'upgrade', label: 'Upgrade to Power', shortLabel: 'Power', icon: <Crown className="h-4 w-4" />, testId: 'tab-agent-upgrade' },
    { id: 'embed', label: 'My Profile & Website', shortLabel: 'Profile', icon: <Code className="h-4 w-4" />, testId: 'tab-agent-embed' },
  ], []);

  const [myListings, setMyListings] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [trialData, setTrialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubscription, setShowSubscription] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, listingsRes, propsRes, trialRes] = await Promise.all([
        axios.get(`${API_URL}/api/agent/verification-status`).catch(() => ({ data: { status: 'not_submitted' } })),
        axios.get(`${API_URL}/api/properties/my-listings`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/properties`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/agent/trial-status`).catch(() => ({ data: null }))
      ]);
      setVerificationStatus(statusRes.data);
      setMyListings(listingsRes.data || []);
      setAllProperties(propsRes.data || []);
      setTrialData(trialRes.data);
    } catch (err) {
      logger.error('Agent dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isVerified = verificationStatus?.agent_verified === true;
  const vStatus = verificationStatus?.status || 'not_submitted';
  const canList = trialData?.can_list !== false;

  const deleteProperty = async (propertyId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/properties/${propertyId}`);
      toast.success('Property deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const releaseListings = async () => {
    if (!window.confirm('Release all your listings? Other agents will be able to claim them.')) return;
    try {
      const res = await axios.post(`${API_URL}/api/agent/release-listings`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to release');
    }
  };

  const claimListing = async (propertyId) => {
    try {
      const res = await axios.post(`${API_URL}/api/agent/claim-listing/${propertyId}`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim');
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] premium-theme" data-testid="agent-dashboard">
      <Header />
      <RoleGuideBanner currentSection="/agent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* CLEAN HEADER — Agent's listings + stats first.
            Upsells (Pulse Report, refer-an-agent, local pros, partner CTA,
            affiliate widget) moved into the "Tools & Offers" drawer below. */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
          <div>
            <span className="text-xs tracking-[0.3em] text-[#E07A3A] mb-1 block uppercase font-medium">Agent Portal</span>
            <h1 className="font-['Playfair_Display'] text-2xl sm:text-4xl text-[#1A1A1A]">Agent Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1">
              Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}. Manage your listings and warm leads.
            </p>
          </div>
          <div className="flex gap-3">
            {isVerified && canList && (
              <Link to="/seller/create">
                <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] tracking-widest text-sm px-8 py-6 font-semibold" data-testid="add-property-btn">
                  <Plus className="h-5 w-5 mr-2" />
                  LIST PROPERTY
                </Button>
              </Link>
            )}
            <Link to="/properties">
              <Button variant="outline" className="border-slate-200 text-slate-600 hover:text-[#E07A3A] hover:border-[#E07A3A] tracking-widest text-sm px-6 py-6 font-semibold" data-testid="browse-properties-btn">
                <Eye className="h-5 w-5 mr-2" />
                BROWSE ALL
              </Button>
            </Link>
          </div>
        </div>

        {/* Verification Banner — important auth state, keep visible. */}
        <AgentVerificationBanner status={vStatus} onSubmit={fetchData} />

        {/* Trial Tracker (only for verified agents) */}
        {isVerified && trialData && <TrialTracker trialData={trialData} onRelease={releaseListings} onSubscribe={() => setShowSubscription(true)} />}

        {/* Tabs */}
        <div className="mt-6">
          <DashboardTabs activeTab={activeTab} onTabChange={setActiveTab} tabs={agentTabs} />
        </div>

        <RecoveryDiscountBanner dataTestId="agent-recovery-banner" />

        {/* MY LISTINGS TAB */}
        {activeTab === 'my-listings' && (
          <div>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="luxury-card p-5 text-center" data-testid="agent-stat-my-total">
                <p className="text-3xl font-semibold text-[#1A1A1A]">{myListings.length}</p>
                <p className="text-xs text-slate-500 mt-1 tracking-wider uppercase">My Listings</p>
              </div>
              <div className="luxury-card p-5 text-center">
                <p className="text-3xl font-semibold text-[#E07A3A]">{myListings.filter(p => p.listing_type === 'sale').length}</p>
                <p className="text-xs text-slate-500 mt-1 tracking-wider uppercase">For Sale</p>
              </div>
              <div className="luxury-card p-5 text-center">
                <p className="text-3xl font-semibold text-[#1A1A1A]">{myListings.filter(p => p.listing_type === 'rent').length}</p>
                <p className="text-xs text-slate-500 mt-1 tracking-wider uppercase">For Rent</p>
              </div>
              <div className="luxury-card p-5 text-center">
                <p className="text-3xl font-semibold text-[#1A1A1A]">{myListings.reduce((sum, p) => sum + (p.views || 0), 0)}</p>
                <p className="text-xs text-slate-500 mt-1 tracking-wider uppercase">Total Views</p>
              </div>
            </div>

            {(() => {
              if (!isVerified && myListings.length === 0) return (
                <div className="luxury-card p-12 text-center">
                  <Shield className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">Get verified to start listing properties</p>
                  <p className="text-xs text-slate-400 mt-2">Submit your credentials above, and once approved you can list properties for sale or rent.</p>
                </div>
              );
              if (loading) return (
                <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" /></div>
              );
              if (myListings.length > 0) return (
              <div className="space-y-4">
                {myListings.map(p => (
                  <div key={p.id} className="luxury-card p-4 flex flex-col sm:flex-row gap-4 items-start" data-testid={`my-listing-${p.id}`}>
                    <div className="w-full sm:w-32 h-24 bg-slate-100 flex-shrink-0 overflow-hidden">
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-300"><Home className="h-8 w-8" /></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-[#1A1A1A] truncate">{p.title}</h3>
                        <span className={`text-[10px] px-2 py-0.5 font-semibold tracking-wider uppercase ${p.status === 'active' ? 'bg-emerald-50 text-emerald-600' : p.status === 'suspended' ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-500'}`}>
                          {p.status}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 font-semibold tracking-wider uppercase ${p.listing_type === 'rent' ? 'bg-blue-50 text-blue-600' : 'bg-[#FEF3EC] text-[#E07A3A]'}`}>
                          {p.listing_type === 'rent' ? 'RENTAL' : 'SALE'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{p.city} | {p.bedrooms} bed | {p.views || 0} views</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Link to={`/property/${p.id}`}>
                        <Button variant="outline" size="sm" className="text-xs"><Eye className="h-3 w-3 mr-1" />View</Button>
                      </Link>
                      <Link to={`/seller/edit/${p.id}`}>
                        <Button variant="outline" size="sm" className="text-xs">Edit</Button>
                      </Link>
                      <Button variant="outline" size="sm" className="text-xs text-red-500 hover:bg-red-50" onClick={() => deleteProperty(p.id, p.title)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            );
              return (
                <div className="luxury-card p-12 text-center">
                  <Home className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">No listings yet</p>
                  {isVerified && canList && (
                    <Link to="/seller/create">
                      <Button className="mt-4 bg-[#E07A3A] text-white hover:bg-[#C4652E]" data-testid="add-first-property-btn">
                        <Plus className="h-4 w-4 mr-2" />List Your First Property
                      </Button>
                    </Link>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* MARKETPLACE TAB */}
        {activeTab === 'marketplace' && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="luxury-card p-5 text-center" data-testid="agent-stat-total">
                <p className="text-3xl font-semibold text-[#1A1A1A]">{allProperties.length}</p>
                <p className="text-xs text-slate-500 mt-1 tracking-wider uppercase">All Properties</p>
              </div>
              <div className="luxury-card p-5 text-center">
                <p className="text-3xl font-semibold text-[#E07A3A]">{allProperties.filter(p => p.listing_type === 'sale').length}</p>
                <p className="text-xs text-slate-500 mt-1 tracking-wider uppercase">For Sale</p>
              </div>
              <div className="luxury-card p-5 text-center">
                <p className="text-3xl font-semibold text-[#1A1A1A]">{allProperties.filter(p => p.listing_type === 'rent').length}</p>
                <p className="text-xs text-slate-500 mt-1 tracking-wider uppercase">For Rent</p>
              </div>
              <div className="luxury-card p-5 text-center">
                <p className="text-3xl font-semibold text-[#1A1A1A]">{[...new Set(allProperties.map(p => p.country))].length}</p>
                <p className="text-xs text-slate-500 mt-1 tracking-wider uppercase">Countries</p>
              </div>
            </div>
            {loading ? (
              <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" /></div>
            ) : allProperties.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allProperties.slice(0, 12).map(p => <PropertyCard key={p.id} property={p} />)}
              </div>
            ) : (
              <div className="luxury-card p-12 text-center">
                <Home className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500">No properties available</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'deals' && <DealTracker userRole="agent" />}
        {activeTab === 'open-listings' && <OpenListingsSection onClaim={claimListing} />}
        {activeTab === 'find-pros' && <FindProfessionals userRole="agent" />}
        {activeTab === 'bulk-upload' && <BulkListingUploader token={authToken} />}
        {/* Availability Tab */}
        {activeTab === 'availability' && (
          <div data-testid="agent-availability-tab">
            <AvailabilityCalendar />
          </div>
        )}

        {activeTab === 'embed' && <EmbedCodeSection />}
        {activeTab === 'subscription' && <SubscriptionPlans />}
        {activeTab === 'upgrade' && <PowerPartnerUpgrade user={user} />}
        {activeTab === 'professional' && <ProfessionalAdvisor userRole="agent" />}

        {/* Help & Guide — collapsible, at bottom */}
        <details className="mt-8" data-testid="agent-help-guide">
          <summary className="cursor-pointer flex items-center gap-2 text-sm text-[#E07A3A] font-semibold tracking-wider uppercase hover:text-[#C4652E] transition-colors py-3">
            <HelpCircle className="h-4 w-4" />
            Help & Getting Started Guide
          </summary>
          <div className="mt-3">
            <GettingStartedGuide role="agent" />
          </div>
        </details>

        {/* ─── TOOLS & OFFERS DRAWER — relocated upsells ─── */}
        <details
          className="mt-10 border border-slate-200 rounded-lg bg-white overflow-hidden"
          open
          data-testid="agent-tools-offers"
        >
          <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between bg-gradient-to-r from-[#FEF8EC] via-white to-[#FEF3EC] border-b border-slate-100 hover:bg-amber-50/40 transition-colors">
            <div className="flex items-center gap-3">
              <Activity className="h-5 w-5 text-[#E07A3A]" />
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E07A3A]">Tools &amp; Offers</p>
                <p className="text-sm font-semibold text-[#0F172A]">Pulse Report, partner CTA &amp; affiliate earnings · click to collapse</p>
              </div>
            </div>
            <span className="text-[10px] text-slate-500 hidden sm:block">All optional</span>
          </summary>

          <div className="p-5 space-y-5">
            <VerifiedPartnerCTA country={user?.country || 'UK'} />

            <Link
              to={`/free-valuation?country=${(user?.country || 'UK').toUpperCase()}&ref=agent-dashboard`}
              className="flex flex-wrap items-center gap-4 bg-gradient-to-r from-[#FEF8EC] via-white to-[#FEF3EC] text-[#0F172A] px-5 py-4 border border-[#E07A3A]/40 hover:border-[#E07A3A] transition-colors group shadow-sm rounded"
              data-testid="agent-pulse-sell-cta"
            >
              <div className="w-10 h-10 bg-[#E07A3A]/10 border border-[#E07A3A]/30 flex items-center justify-center flex-shrink-0">
                <Activity className="h-5 w-5 text-[#E07A3A]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-[#E07A3A] tracking-[0.2em] uppercase font-bold mb-0.5">
                  Win the Listing · £49 / ₹4,900 / C$79
                </p>
                <p className="text-sm font-semibold text-[#0F172A] truncate">
                  Order an independent Pulse Investment Report for your next vendor pitch
                </p>
              </div>
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-white bg-[#E07A3A] px-3 py-1.5 group-hover:bg-[#C4652E] transition-colors flex-shrink-0" data-testid="agent-pulse-sell-cta-buy-btn">
                Buy Report
              </span>
              <ArrowRight className="h-4 w-4 text-[#E07A3A] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            </Link>

            <Link
              to="/for-agents"
              className="flex flex-wrap items-center gap-4 bg-gradient-to-r from-[#0F172A] to-[#1e293b] text-white px-5 py-4 border border-[#E07A3A]/30 hover:border-[#E07A3A] transition-colors group rounded"
              data-testid="agent-refer-cta"
            >
              <div className="w-10 h-10 bg-[#25D366]/20 border border-[#25D366]/40 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="h-5 w-5 text-[#25D366]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-[#E07A3A] tracking-[0.2em] uppercase font-bold mb-0.5">Refer Another Agent · WhatsApp Ready</p>
                <p className="text-sm font-semibold truncate">Share Shopperden with agents in your network — unlock Priority Listing Boost at 5 invites</p>
              </div>
              <ArrowRight className="h-4 w-4 text-[#E07A3A] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            </Link>

            <LocalProsPanel
              role="agent"
              city={user?.city || ''}
              country={(user?.country || 'UK').toUpperCase()}
              dataTestId="agent-local-pros-panel"
            />

            <InviteTeaserCard />

            <AffiliateWidget dataTestId="agent-affiliate-widget" />
          </div>
        </details>
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscription}
        onClose={() => setShowSubscription(false)}
        onSuccess={() => { setShowSubscription(false); fetchData(); }}
      />
    </div>
  );
};

export default AgentDashboard;
